import http from "@/services/http";
import {
  ContactListParams,
  ContactListItemDto,
  ContactResponseDto,
  CreateContactDto,
  UpdateContactDto,
  CreateContactAddressDto,
  ContactAddressResponseDto,
  CreateContactMethodDto,
  ContactMethodResponseDto,
  CreateContactEmploymentDto,
  ContactEmploymentResponseDto,
  CreateContactReferenceDto,
  ContactReferenceResponseDto,
  PagedResult,
} from "@/models/contact.model";

export interface ContactListResponse {
  items: ContactListItemDto[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

class ContactService {
  // ==================== CONTACTS ====================

  async listContacts(params: ContactListParams = {}): Promise<ContactListResponse> {
    const query = new URLSearchParams();
    const searchTerm = params.search?.trim();
    if (searchTerm) {
      query.set("search", searchTerm);
      query.set("texto", searchTerm);
    }
    if (params.page) query.set("page", String(params.page));
    if (params.pageSize) query.set("pageSize", String(params.pageSize));
    const qs = query.toString();

    const response = await http.get<
      PagedResult<ContactListItemDto> | ContactListItemDto[]
    >(`/contacts${qs ? `?${qs}` : ""}`);

    if (Array.isArray(response)) {
      return {
        items: response,
        total: response.length,
        page: 1,
        pageSize: response.length,
        totalPages: 1,
      };
    }

    const items = response.items || [];
    const totalFromResponse =
      typeof (response as any).totalCount === "number"
        ? (response as any).totalCount
        : typeof (response as any).total === "number"
          ? (response as any).total
          : typeof (response as any).totalItems === "number"
            ? (response as any).totalItems
            : undefined;
    const page =
      typeof (response as any).page === "number"
        ? (response as any).page
        : typeof (response as any).pageNumber === "number"
          ? (response as any).pageNumber
          : 1;
    const pageSize =
      typeof (response as any).pageSize === "number"
        ? (response as any).pageSize
        : typeof (response as any).page_size === "number"
          ? (response as any).page_size
          : items.length || 10;

    return {
      items,
      total: totalFromResponse ?? items.length,
      page,
      pageSize,
      totalPages: (response as any).totalPages || 1,
    };
  }

  async getContactById(contactId: string): Promise<ContactResponseDto> {
    return http.get<ContactResponseDto>(`/contacts/${contactId}`);
  }

  async getContactByIdentifier(identifier: string): Promise<ContactResponseDto> {
    return http.get<ContactResponseDto>(`/contacts/${identifier}`);
  }

  async createContact(data: CreateContactDto): Promise<ContactResponseDto> {
    return http.post<ContactResponseDto>("/contacts", data);
  }

  async updateContact(contactId: string, data: UpdateContactDto): Promise<ContactResponseDto> {
    return http.put<ContactResponseDto>(`/contacts/${contactId}`, data);
  }

  async updateContactFull(contactId: string, data: CreateContactDto): Promise<ContactResponseDto> {
    return http.put<ContactResponseDto>(`/contacts/${contactId}`, data);
  }

  async deleteContact(contactId: string): Promise<void> {
    await http.delete<void>(`/contacts/${contactId}`);
  }

  // ==================== ADDRESSES ====================

  async addAddress(
    contactId: string,
    data: CreateContactAddressDto
  ): Promise<ContactAddressResponseDto> {
    return http.post<ContactAddressResponseDto>(
      `/contacts/${contactId}/addresses`,
      data
    );
  }

  async updateAddress(
    contactId: string,
    addressId: string,
    data: CreateContactAddressDto
  ): Promise<ContactAddressResponseDto> {
    return http.put<ContactAddressResponseDto>(
      `/contacts/${contactId}/addresses/${addressId}`,
      data
    );
  }

  async deleteAddress(contactId: string, addressId: string): Promise<void> {
    await http.delete<void>(`/contacts/${contactId}/addresses/${addressId}`);
  }

  // ==================== CONTACT METHODS ====================

  async addContactMethod(
    contactId: string,
    data: CreateContactMethodDto
  ): Promise<ContactMethodResponseDto> {
    return http.post<ContactMethodResponseDto>(
      `/contacts/${contactId}/contact-methods`,
      data
    );
  }

  async updateContactMethod(
    contactId: string,
    methodId: string,
    data: CreateContactMethodDto
  ): Promise<ContactMethodResponseDto> {
    return http.put<ContactMethodResponseDto>(
      `/contacts/${contactId}/contact-methods/${methodId}`,
      data
    );
  }

  async deleteContactMethod(contactId: string, methodId: string): Promise<void> {
    await http.delete<void>(`/contacts/${contactId}/contact-methods/${methodId}`);
  }

  // ==================== EMPLOYMENTS ====================

  async addEmployment(
    contactId: string,
    data: CreateContactEmploymentDto
  ): Promise<ContactEmploymentResponseDto> {
    return http.post<ContactEmploymentResponseDto>(
      `/contacts/${contactId}/employments`,
      data
    );
  }

  async updateEmployment(
    contactId: string,
    employmentId: string,
    data: CreateContactEmploymentDto
  ): Promise<ContactEmploymentResponseDto> {
    return http.put<ContactEmploymentResponseDto>(
      `/contacts/${contactId}/employments/${employmentId}`,
      data
    );
  }

  async deleteEmployment(contactId: string, employmentId: string): Promise<void> {
    await http.delete<void>(`/contacts/${contactId}/employments/${employmentId}`);
  }

  // ==================== REFERENCES ====================

  async addReference(
    contactId: string,
    data: CreateContactReferenceDto
  ): Promise<ContactReferenceResponseDto> {
    return http.post<ContactReferenceResponseDto>(
      `/contacts/${contactId}/references`,
      data
    );
  }

  async updateReference(
    contactId: string,
    referenceId: string,
    data: CreateContactReferenceDto
  ): Promise<ContactReferenceResponseDto> {
    return http.put<ContactReferenceResponseDto>(
      `/contacts/${contactId}/references/${referenceId}`,
      data
    );
  }

  async deleteReference(contactId: string, referenceId: string): Promise<void> {
    await http.delete<void>(`/contacts/${contactId}/references/${referenceId}`);
  }
}

export default new ContactService();
